import { useState } from "react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { IMAGES } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";

const TEXT_FIELDS = [
  ["owner_name", "Owner Name *", true], ["company", "Company", false], ["email", "Email *", true],
  ["phone", "Phone", false], ["property_address", "Property Address *", true], ["county", "County", false],
  ["state", "State", false], ["parcel_number", "Parcel Number", false], ["acreage", "Acreage", false],
  ["asking_price", "Asking Price", false], ["property_type", "Property Type", false], ["zoning", "Zoning", false],
];

const UTILITY_FIELDS = [
  ["power_info", "Power Information"], ["substation_info", "Substation Information"], ["transmission_lines", "Transmission Lines"],
  ["natural_gas", "Natural Gas"], ["water", "Water"], ["sewer", "Sewer"], ["fiber", "Fiber"],
  ["rail", "Rail"], ["interstate_access", "Interstate Access"], ["environmental_history", "Environmental History"],
  ["existing_structures", "Existing Structures"],
];

export default function SiteSubmission() {
  useSeo("Submit a Site | 12 Stone Development Group",
    "Have land with development potential? Submit your property to 12 Stone Development Group for evaluation. Open to landowners, brokers, economic development agencies, municipalities, utilities and industrial property owners.");
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, { name: f.name, storage_path: data.storage_path }]);
      } catch { toast.error(`Failed to upload ${f.name}`); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!authorized) { toast.error("Please authorize evaluation to continue."); return; }
    setLoading(true);
    try {
      await api.post("/leads", {
        lead_type: "site_submission",
        name: form.owner_name || "", company: form.company || "", email: form.email || "",
        phone: form.phone || "", property_address: form.property_address || "",
        category: form.property_type || "Land Submission", message: form.comments || "",
        payload: form, files, source: "/site-submission",
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch { toast.error("Submission failed. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div>
      <PageHero overline="Site Submission" title="Submission Received" image={IMAGES.aerialLand} height="min-h-[45vh]" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-5" />
        <h2 className="font-display text-2xl font-bold text-foreground">Thank you for your submission</h2>
        <p className="text-muted-foreground mt-3">12 Stone Development Group will evaluate this property for potential development opportunities and follow up with you.</p>
      </div>
    </div>
  );

  const inputCls = "mt-1.5 bg-secondary/50 border-border";

  return (
    <div>
      <PageHero overline="Site Submission" title="Have Land With Development Potential?"
        subtitle="We accept submissions from landowners, brokers, economic development agencies, municipalities, utilities and industrial property owners."
        image={IMAGES.aerialLand} />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <form onSubmit={submit} data-testid="site-submission-form" className="space-y-10">
          <Reveal>
            <Overline>Property & Contact</Overline>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TEXT_FIELDS.map(([k, label, req]) => (
                <div key={k} className={k === "property_address" ? "sm:col-span-2" : ""}>
                  <Label className="text-xs text-muted-foreground">{label}</Label>
                  <Input required={req} type={k === "email" ? "email" : "text"} value={form[k] || ""} onChange={set(k)} data-testid={`site-${k}`} className={inputCls} />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <Overline>Infrastructure & Utilities</Overline>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {UTILITY_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs text-muted-foreground">{label}</Label>
                  <Input value={form[k] || ""} onChange={set(k)} data-testid={`site-${k}`} className={inputCls} />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <Overline>Documents</Overline>
            <p className="text-xs text-muted-foreground mt-2">Upload maps, utility letters, surveys, photos and other documents (PDF, images).</p>
            <label className="mt-4 flex items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-secondary/30 p-6 cursor-pointer hover:border-primary/40 transition-colors" data-testid="site-upload-label">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Upload className="h-5 w-5 text-primary" />}
              <span className="text-sm text-muted-foreground">{uploading ? "Uploading..." : "Click to upload documents"}</span>
              <input type="file" multiple className="hidden" onChange={onFiles} data-testid="site-file-input" />
            </label>
            {files.length > 0 && (
              <ul className="mt-4 space-y-2">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-2.5">
                    <span className="flex items-center gap-2 text-sm text-foreground"><FileText className="h-4 w-4 text-primary" /> {f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-400"><X className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </Reveal>

          <Reveal>
            <Label className="text-xs text-muted-foreground">Comments</Label>
            <Textarea value={form.comments || ""} onChange={set("comments")} rows={4} data-testid="site-comments" className={inputCls} />
          </Reveal>

          <Reveal>
            <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-5">
              <Checkbox id="auth" checked={authorized} onCheckedChange={setAuthorized} data-testid="site-authorize" className="mt-0.5" />
              <label htmlFor="auth" className="text-sm text-foreground/90 leading-relaxed cursor-pointer">
                I authorize 12 Stone Development Group to evaluate this property for potential development opportunities.
              </label>
            </div>
            <Button type="submit" disabled={loading || uploading} data-testid="site-submission-submit-btn" className="mt-6 w-full bg-primary hover:bg-primary/90 text-white font-semibold rounded-full h-12">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "SUBMIT PROPERTY FOR EVALUATION"}
            </Button>
          </Reveal>
        </form>
      </section>
    </div>
  );
}
